import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';
import { distinctUntilChanged, filter, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostCenterCostUnitDistribution } from '../../../models/cost-center-cost-unit-distribution.model';
import { IJoinCostCenterCostUnitState } from '../../../services/facades/master-data/join-cost-center-cost-unit-state.model';
import { JoinCostCenterCostUnitFacade } from '../../../services/facades/master-data/join-cost-center-cost-unit.facade';
import { JoinCostCenterCostUnitDialogComponent } from './join-cost-center-cost-unit-dialog/join-cost-center-cost-unit-dialog.module';
import { TransferCostCenterCostUnitDialogComponent } from './transfer-cost-center-cost-unit-dialog/transfer-cost-center-cost-unit-dialog.component';

@Component({
    templateUrl: './join-cost-center-cost-unit.page.html',
    styleUrls: [ './join-cost-center-cost-unit.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JoinCostCenterCostUnitPage implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild(MatPaginator)
    public paginator!: MatPaginator;
    @ViewChild(MatSort)
    public sort: MatSort;
    public dataSource: MatTableDataSource<ICostCenterCostUnitDistribution> = new MatTableDataSource<ICostCenterCostUnitDistribution>([]);
    public selectFormGroup: FormGroup = new FormGroup({
        selectedDaterange: new FormGroup({ start: new FormControl(null), end: new FormControl(null) }),
        selectedCostCenter: new FormControl(null),
        selectedDistributionReference: new FormControl(null),
        selectedCostAccountingType: new FormControl(null),
    });

    public changedCalculationOrders = new Set([]);
    public state$: Observable<IJoinCostCenterCostUnitState>;
    private destroy$: Subject<void> = new Subject();

    constructor(private myCostCenterDistributionFacade: JoinCostCenterCostUnitFacade,
                private myMatDialog: MatDialog) {
    }

    public ngOnInit(): void {

        void this.myCostCenterDistributionFacade.fetchAssignmentsIfFiltersAreValid();
        this.state$ = this.myCostCenterDistributionFacade.subState();
        this.state$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IJoinCostCenterCostUnitState) => s.assignments),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((assignments: ICostCenterCostUnitDistribution[]) => {
                this.dataSource.data = assignments;
            });
        this.state$
            .pipe(
                takeUntil(this.destroy$),
                map((s: IJoinCostCenterCostUnitState) => s.selectedDistributionReference),
                distinctUntilChanged())
            .subscribe((selectedDistributionReference: number) => {
                this.selectFormGroup.controls[ 'selectedDistributionReference' ].setValue(selectedDistributionReference);
            });
        void this.myCostCenterDistributionFacade.fetchData();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.myCostCenterDistributionFacade.clearState();
    }

    public ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (item: ICostCenterCostUnitDistribution, tableColumnName: string): string | number => {
            switch (tableColumnName) {
                case 'costUnitName':
                    return item.costUnit.name;
                case 'costUnitArticleNr':
                    return item.costUnit.articleNr;
                default:
                    return item[ tableColumnName ];
            }
        };

        this.dataSource.filterPredicate = (assignment: ICostCenterCostUnitDistribution, filterString: string): boolean => {
            const filterValue: string = filterString.trim().toLowerCase();
            return assignment.costUnit.name.toLowerCase().includes(filterValue)
                || assignment.costUnit.articleNr.toLowerCase().includes(filterValue);
        };

        this.listenToCostCenterSelectionChange();
        this.listenToCostAccountingTypeSelectionChange();
        this.listenToDateSelectionChange();
        this.listenToDistributionReferenceSelectionChange();
    }

    public addKostentraegerToKostenstelle(): void {
        const stateSnapshot: IJoinCostCenterCostUnitState = this.myCostCenterDistributionFacade.getStateSnapshot();
        const dialog: MatDialogRef<JoinCostCenterCostUnitDialogComponent> = this.myMatDialog.open(JoinCostCenterCostUnitDialogComponent, {
            width: '600px',
            data: {
                costAccountingTypeId: stateSnapshot.selectedCostAccountingType,
                validFrom: stateSnapshot.selectedValidFrom,
                validTo: stateSnapshot.selectedValidTo,
            },
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void this.myCostCenterDistributionFacade.fetchAssignmentsIfFiltersAreValid();
            }
        });
    }

    public onEditDistributionValidity(distribution: ICostCenterCostUnitDistribution): void {
        const dialog: MatDialogRef<JoinCostCenterCostUnitDialogComponent> = this.myMatDialog.open(JoinCostCenterCostUnitDialogComponent, {
            width: '600px',
            data: distribution,
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void this.myCostCenterDistributionFacade.fetchAssignmentsIfFiltersAreValid();
            }
        });
    }

    public checkOrderChange(distribution: ICostCenterCostUnitDistribution, order: number): void {
        if (Number.isNaN(Number(order))) {
            return;
        }

        if (+order !== distribution.calculationOrder) {
            this.changedCalculationOrders.add(distribution.id);
            distribution.calculationOrder = +order;
        }
    }

    public async onChangeCalculationOrder(distribution: ICostCenterCostUnitDistribution): Promise<void> {
        if (this.changedCalculationOrders.has(distribution.id)) {
            await this.myCostCenterDistributionFacade.updateCalculationOrder(distribution.id, distribution.calculationOrder);
            this.changedCalculationOrders.delete(distribution.id);
        }
    }

    public onDeleteDistribution(element: ICostCenterCostUnitDistribution): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {

            data: {
                message: 'app.join-cost-center-cost-unit.confirm-delete-group.label',
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myCostCenterDistributionFacade.deleteDistribution(element);
                void this.myCostCenterDistributionFacade.fetchAssignmentsIfFiltersAreValid();
            }
        });
    }

    public onClickTransfer(): void {
        this.myMatDialog.open(TransferCostCenterCostUnitDialogComponent, {
            width: '40%',
        });
    }

    public searchCostUnit(value: string): void {
        this.dataSource.filter = value.trim().toLowerCase();
    }

    private listenToCostCenterSelectionChange(): void {
        this.selectFormGroup.controls[ 'selectedCostCenter' ].valueChanges.subscribe((costCenterId: number) => {
            void this.myCostCenterDistributionFacade.setSelectedKostenstelle(costCenterId);
        });
    }

    private listenToCostAccountingTypeSelectionChange(): void {
        this.selectFormGroup.controls[ 'selectedCostAccountingType' ].valueChanges.subscribe((costAccountingTypeId: number) => {
            void this.myCostCenterDistributionFacade.setSelectedCostAccountingType(costAccountingTypeId);
        });
    }

    private listenToDateSelectionChange(): void {
        this.selectFormGroup.controls[ 'selectedDaterange' ].valueChanges.subscribe((daterange) => {
            void this.myCostCenterDistributionFacade.setSelectedDaterange(daterange.start, daterange.end);
        });
    }

    private listenToDistributionReferenceSelectionChange(): void {

        this.selectFormGroup.controls[ 'selectedDistributionReference' ].valueChanges
            .pipe(filter(d => d !== null))
            .subscribe((distributionReferenceId: number) => {
                const stateSnapshot: IJoinCostCenterCostUnitState = this.myCostCenterDistributionFacade.getStateSnapshot();
                if (!isNullOrUndefined(stateSnapshot.selectedCostCenter)
                    && !isNullOrUndefined(stateSnapshot.selectedCostAccountingType)
                    && stateSnapshot.selectedDistributionReference !== distributionReferenceId) {
                    void this.myCostCenterDistributionFacade.setSelectedDistributionReference(distributionReferenceId);
                }
            });
    }
}
